import { WebStorageStateStore } from 'oidc-client-ts';
import type { AuthProviderProps } from 'react-oidc-context';
import { env } from '@config/env';
import { authStore } from './authStorage';

// Cognito redirect/logout are derived from the CURRENT host at runtime instead of a build-time env
// var, so the same deployed bundle works on any origin it's served from (dev., app.,
// localhost) — each origin just has to be registered as a callback/logout URL on the Cognito client.
// This is what lets us move the dashboard to a new hostname without rebuilding the FE.
const callbackUri = `${window.location.origin}/auth/callback`;
const logoutUri   = `${window.location.origin}/`;

export const oidcConfig: AuthProviderProps = {
  authority:                env.cognitoAuthority,
  client_id:                env.cognitoClientId,
  redirect_uri:             callbackUri,
  post_logout_redirect_uri: logoutUri,
  response_type:            'code',
  scope:                    'openid profile email',
  // MUST stay false. Our in-app SRP sign-in (bridgeCognitoSession) mints access tokens that Cognito's
  // `/oauth2/userInfo` endpoint 401s on — the SAME reason the BE needed a `cognito-idp GetUser`
  // fallback (journal-backend commit 66a1d62). With loadUserInfo:true, oidc-client-ts calls userInfo
  // after every silent renew; that 401 makes it discard the OTHERWISE-SUCCESSFUL refresh, so the
  // session dies mid-use and every /api call then 401s. The profile is sourced from the id_token
  // claims instead (bridgeCognitoSession sets it on login; the refresh grant returns a fresh id_token),
  // which carries everything the app reads (email / username in ProfileMenu).
  loadUserInfo:             false,
  automaticSilentRenew:     true,
  monitorSession:           false,
  // Resolved ONCE at boot from the device's "Remember me" choice (authStorage). Sign-in performs a
  // full-page navigation afterwards, so the UserManager always hydrates from the store the user just
  // picked. Was hardcoded to sessionStorage, which is why the refresh token died with the browser
  // and every user re-typed their password daily (2026-08-17 report).
  userStore:                new WebStorageStateStore({ store: authStore() }),
  onSigninCallback: () => {
    window.history.replaceState({}, document.title, window.location.pathname);
  },
};

export function buildCognitoSignoutUrl(): string {
  const params = new URLSearchParams({
    client_id:  env.cognitoClientId,
    logout_uri: logoutUri,
  });
  return `${env.cognitoHostedUi}/logout?${params.toString()}`;
}
